import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { SelectModule } from "primeng/select";

/**
 * Opzione per la select
 */
export interface SimpleSelectOption {
  /** Chiave univoca dell'opzione */
  key: string;
  /** Label da mostrare */
  value: string;
}

/**
 * Evento emesso quando l'utente seleziona un'opzione
 */
export interface SimpleSelectChangeEvent {
  /** Evento originale (assente se la selezione non deriva da un'interazione utente) */
  originalEvent?: Event;
  /** Opzione selezionata */
  selectedOption: SimpleSelectOption;
}

/**
 * Componente per una select semplice con stile personalizzato.
 * Wrapper su p-select di PrimeNG.
 *
 * @example
 * ```typescript
 * options: SimpleSelectOption[] = [
 *   { key: "all", value: "Tutte le notifiche" },
 *   { key: "unread", value: "Non lette" },
 *   { key: "read", value: "Lette" }
 * ];
 *
 * onSelectionChange(event: SimpleSelectChangeEvent): void {
 *   console.log("Opzione selezionata:", event.selectedOption);
 * }
 * ```
 *
 * ```html
 * <ape-simple-select
 *   [options]="options"
 *   (selectionChange)="onSelectionChange($event)" />
 * ```
 */
@Component({
  selector: "ape-simple-select",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SelectModule
  ],
  templateUrl: "./ape-simple-select.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./ape-simple-select.component.css"]
})
export class ApeSimpleSelectComponent {
  @Input({ required: true })
  options!: SimpleSelectOption[];

  @Input()
  placeholder: string = "Seleziona un'opzione";

  @Output()
  selectionChange = new EventEmitter<SimpleSelectChangeEvent>();

  @Input()
  selectedOption: SimpleSelectOption | null = null;

  protected onSelectionChange(event: { originalEvent?: Event }): void {
    if (this.selectedOption) {
      this.selectionChange.emit({
        originalEvent: event.originalEvent,
        selectedOption: this.selectedOption
      });
    }
  }

  public getSelectedOption(): SimpleSelectOption | null {
    return this.selectedOption;
  }

  public setSelectedOption(key: string): void {
    const option = this.options.find((opt) => opt.key === key);
    if (option) {
      this.selectedOption = option;
    }
  }
}

